// src/components/seo.component.js

import React from "react";
import { Helmet } from "react-helmet";
import constants from "../constants/constants";
import pageSEO from "../constants/pageSEO";

/**
 * Unified SEO configuration component
 * Can be referenced in each page to set page-specific meta tags
 *
 * @param {Object} props Component properties
 * @param {string} props.title Page title
 * @param {string} props.description Page description
 * @param {string} props.keywords Page keywords
 * @param {string} props.path Current page path (without domain)
 * @param {string} props.image Social sharing image address
 * @param {string} props.type Open Graph type
 * @param {React.ReactNode} props.children Can pass in additional head tags, such as structured data
 */
const SEO = ({
  title,
  description,
  keywords,
  path,
  image,
  type = "website",
  children,
}) => {
  // build the full title
  const fullTitle = title
    ? `${title} | ${constants.BROKERAGE_NAME}`
    : constants.BROKERAGE_NAME;

  // build the full site URL
  const baseUrl =
    constants.ENVIRONMENT === "production"
      ? `https://${constants.DOMAIN}`
      : `http://${constants.URL}`;

  // build the normalized URL
  const url = path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;

  // handle the image URL
  const imageUrl = image
    ? image.startsWith("http")
      ? image
      : `${baseUrl}${image}`
    : `${baseUrl}/logo.png`;

  return (
    <Helmet>
      {/* basic meta data */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* normalize the link */}
      <link rel="canonical" href={url} />

      {/* Open Graph tags - for social media sharing */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={constants.BROKERAGE_NAME} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />

      {/* mobile device meta data */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* robots instructions */}
      <meta name="robots" content="index, follow" />

      {/* other custom meta tags */}
      {children}
    </Helmet>
  );
};

/**
 * Default SEO configuration component
 * Used in App.js to provide default SEO configuration for all pages
 */
export const DefaultSEO = () => {
  const baseUrl =
    constants.ENVIRONMENT === "production"
      ? `https://${constants.DOMAIN}`
      : `http://${constants.URL}`;

  // default description extracted from brokerage introduction
  const defaultDescription = constants.ABOUT
    ? constants.ABOUT.split("<|>")[0] // use the first paragraph as the default description
    : `${constants.BROKERAGE_NAME} provides professional real estate services to help you buy and sell properties.`;

  return (
    <Helmet>
      {/* default title and description */}
      <title>{constants.BROKERAGE_NAME} - Real Estate Services</title>
      <meta name="description" content={defaultDescription} />
      <meta
        name="keywords"
        content="real estate, realtor, real estate agent, property, home, house, real estate services"
      />

      {/* default OG tags */}
      <meta
        property="og:title"
        content={`${constants.BROKERAGE_NAME} - Real Estate Services`}
      />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={baseUrl} />
      <meta property="og:image" content={`${baseUrl}/logo.png`} />
      <meta property="og:site_name" content={constants.BROKERAGE_NAME} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={`${constants.BROKERAGE_NAME} - Real Estate Services`}
      />
      <meta name="twitter:description" content={defaultDescription} />
      <meta name="twitter:image" content={`${baseUrl}/logo.png`} />

      {/* basic meta data */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="UTF-8" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />

      {/* website icon */}
      <link rel="icon" href="/century21-removebg-preview.png" />
      <link rel="apple-touch-icon" href="/logo192.png" />

      {/* normalize the link */}
      <link rel="canonical" href={baseUrl} />

      {/* brokerage structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          name: constants.BROKERAGE_NAME,
          telephone: constants.BROKERAGE_NUMBER,
          email: constants.BROKERAGE_EMAIL,
          url: baseUrl,
          logo: `${baseUrl}/logo.png`,
          description: defaultDescription,
          address: {
            "@type": "PostalAddress",
            addressCountry: "CA",
          },
          sameAs: Object.values(constants.SOCIALS).filter(
            (url) => url && url.length > 0
          ),
        })}
      </script>
    </Helmet>
  );
};

export { pageSEO };
export { SEO };
